'use client';
import * as Sentry from '@sentry/nextjs';
import React, { useEffect } from 'react';

import Error403 from '@/components/app/error/403';
import Error404 from '@/components/app/error/404';
import Error410 from '@/components/app/error/410';
import Error429 from '@/components/app/error/429';
import Error500 from '@/components/app/error/500';

export default function Error({ error }: { error: Error & { digest?: string } }) {
  const getStatusCodeFromError = (err: Error & { digest?: string }) => {
    console.log('Error details:', {
      name: err.name,
      message: err.message,
      statusCode: (err as any).statusCode,
      digest: err.digest,
      stack: err.stack
    });

    // Check for digest-based error identification  
    if (err.digest === 'GONE_410') {
      return 410;
    }
    
    // Check for special archive gone error
    if (err.message === 'ARCHIVE_GONE_410') {
      return 410;
    }

    // Check if error has statusCode property
    if ((err as any).statusCode) {
      return (err as any).statusCode;
    }
    
    // Check if error name is HttpError and has statusCode
    if (err.name === 'HttpError' && (err as any).statusCode) {
      return (err as any).statusCode;
    }
    
    // Check if error name is GoneError
    if (err.name === 'GoneError') {
      return 410;
    }
    
    // Check if error name is ProductionGoneError
    if (err.name === 'ProductionGoneError') {
      return 410;
    }
    
    // Check message for status code pattern
    if (err.message.includes('Status code: ')) {
      const match = err.message.match(/Status code: (\d+)/);
      if (match) {
        return Number(match[1]);
      }
    }
    
    // Check for specific error messages
    if (err.message.includes('410') || err.message.includes('Gone')) {
      return 410;
    }
    
    return 500;
  };

  const statusCode = getStatusCodeFromError(error);

  const errorComponents = {
    403: <Error403 />,
    404: <Error404 />,
    410: <Error410 />,
    429: <Error429 />,
    500: <Error500 />,
  };

  useEffect(() => {
    if (!errorComponents[statusCode] || statusCode >= 500) {
      Sentry.captureException(error);
    }
    console.error('Error caught by error boundary:', error);
  }, [error, statusCode]);

  return errorComponents[statusCode] || errorComponents[500];
} 